import numeral from 'numeral';
import { Currency } from 'src/@types/common';

// ----------------------------------------------------------------------

type InputValue = string | number | null;

export function fNumber(number: InputValue) {
  return numeral(number).format();
}

/** Возвращает 4, если у числа больше 2 знаков после запятой, иначе 2 (для отображения цены/суммы). */
export function fCurrencyDigits(value: InputValue): 2 | 4 {
  if (value == null || Number.isNaN(Number(value))) return 2;
  const scaled = Number(value) * 100;
  if (Math.abs(scaled - Math.round(scaled)) > 1e-9) return 4;
  return 2;
}

/** Округляет сумму/цену: если после запятой по сути 4 знака — до 4, иначе до 2. */
export function roundToMoneyScale(value: InputValue): number {
  const num = Number(value);
  if (value == null || Number.isNaN(num)) return 0;
  const digits = fCurrencyDigits(num);
  const factor = digits === 4 ? 10000 : 100;
  return Math.round(num * factor) / factor;
}

/** До 4 знаков после запятой (для импорта из PDF и полей с decimalScale=4). Сохраняет 12.1599, а не 12.16. */
export function roundTo4Decimals(value: InputValue): number {
  const num = Number(value);
  if (value == null || Number.isNaN(num)) return 0;
  return Math.round(num * 10000) / 10000;
}

export function fCurrency(number: InputValue | undefined, currency: Currency = 'USD', digits: number = 2) {
  const num = Number(number) || 0;

  if (number === null || (!Number(number) && Number(number) !== 0)) {
    return '';
  }

  let format: any = num;

  if (currency === 'USD') {
    format = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  }

  if (currency === 'RUB') {
    format = new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  }

  if (currency === 'BYN') {
    format = new Intl.NumberFormat('be-BY', {
      style: 'currency',
      currency: 'BYN',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  }

  if (currency === 'KZT') {
    format = new Intl.NumberFormat('kk-KZ', {
      style: 'currency',
      currency: 'KZT',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  }

  if (currency === 'EUR') {
    format = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  }

  if (currency === 'CNY') {
    format = new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  }

  return format;
}

/** Форматирование по ISO 4217; неизвестные коды — через Intl, при ошибке — RUB. */
export function fCurrencyByCode(
  number: InputValue | undefined,
  currencyCode: string | null | undefined,
  digits?: number
) {
  const code = (currencyCode || 'RUB').toUpperCase();
  const d = digits ?? fCurrencyDigits(number ?? null);
  const known: Currency[] = ['RUB', 'USD', 'EUR', 'CNY', 'BYN', 'KZT'];
  if (known.includes(code as Currency)) {
    return fCurrency(number, code as Currency, d);
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(Number(number) || 0);
  } catch {
    return fCurrency(number, 'RUB', d);
  }
}

export function fPercent(number: InputValue) {
  const format = number ? numeral(Number(number) / 100).format('0.0%') : '';

  return result(format, '.0');
}

export function fShortenNumber(number: InputValue) {
  const format = number ? numeral(number).format('0.00a') : '';

  return result(format, '.00');
}

export function fData(number: InputValue) {
  const format = number ? numeral(number).format('0.0 b') : '';

  return result(format, '.0');
}

function result(format: string, key = '.00') {
  const isInteger = format.includes(key);

  return isInteger ? format.replace(key, '') : format;
}


export function calculateVolume(length: number, width: number, height: number): number {
  if (length <= 0 || width <= 0 || height <= 0) {
    return 0;
  }

  const volumeCm = length * width * height; // Объем в кубических сантиметрах
  return volumeCm / 1_000_000; // Объем в кубических метрах
}
